import React, { useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { constant } from '@medflex/commons';
import Logger from '@medflex/utils/Logger';
import InputField from '@medflex/components/stories/InputField';
import Button from '@medflex/components/stories/Button';
import DropDown from '@medflex/components/stories/DropDown';
import Spinner from '@medflex/components/stories/Spinner';
import ProfileItems, { ProfileItem, SubTitle } from '@medflex/components/stories/ProfileItems';
import { IconUserSolidSquare } from '@medflex/components/stories/Icons';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { trackEvent } from '@medflex/utils/Tracking';
import MultiCheckboxSelect from '@medflex/components/stories/MultiCheckboxSelect';
import _ from 'lodash/fp';
import useUserProfile from '../hooks/useUserProfile';
import useCountries from '../hooks/useCountries';
import useModal from '../hooks/useModal';

const CUSTOM_WORK_AREA_SELECTION = 'Anderes Fachgebiet';

const isCustomWorkArea = workArea => {
  return (
    (workArea && !_.flattenDeep(constant.workAreaValues).includes(workArea)) ||
    workArea === CUSTOM_WORK_AREA_SELECTION
  );
};

const validationSchema = yup.object().shape({
  areaOfActivity: yup.string().required(),
  organizationalForm: yup.string(),
  usedFeatures: yup.array().of(yup.string()),
  address: yup.string(),
  address2: yup.string(),
  postalCode: yup.string(),
  workplace: yup.string(),
  city: yup.string(),
});

const usedFeaturesFieldData = Object.keys(constant.usedFeatures).reduce(
  (res, area) => ({
    values: [...res.values, area],
    labels: [...res.labels, constant.usedFeatures[area]],
  }),
  { values: [], labels: [] },
);

const RulerOption = () => <option disabled>-------</option>;
const NoSelectionOption = ({ children }) => {
  const { t } = useTranslation('physician');
  return (
    <option value="none">
      {children || t('PhysicianClinicProfileEditModule.form.no-selection')}
    </option>
  );
};

NoSelectionOption.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

const PhysicianClinicProfileEditModule = () => {
  const { close } = useModal();
  const dispatch = useDispatch();
  const { userProfile, update, isUpdating } = useUserProfile();
  const {
    register: registerFormElement,
    handleSubmit: wrapSubmit,
    formState,
    control,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const selectedWorkArea = useWatch({ control, name: 'workArea1' });

  const { t } = useTranslation('physician');
  const userProfileErrors = useSelector(e => e.errors.userProfile);
  const countries = useCountries();
  const {
    areaOfActivity,
    workArea1,
    address,
    address2,
    postalCode,
    city,
    countryCode,
    organizationalForm,
    usedFeatures,
    workplace,
  } = userProfile.attributes;

  const showGenericError = () => {
    showAlertToast(t('PhysicianClinicProfileEditModule.unexpected_error.title'), null, {
      toastId: 'profileEditFailed',
    });
  };

  const isSubmitDisabled = !formState.isValid || isUpdating;

  useEffect(() => {
    if (userProfileErrors) {
      Logger.error(`PhysicianClinicProfileEditModule: ${JSON.stringify(userProfileErrors)}`);
      showGenericError();
      dispatch.errors.clear();
    }
  }, [userProfileErrors]);

  const handleSubmit = async (formData, event) => {
    const netData = Object.entries(formData).reduce((memo, [key, value]) => {
      if (
        (key === 'workArea1' && !formData.workArea1) ||
        (key === 'mobileNumber' && !formData.mobileNumber) ||
        (key === 'usedFeatures' && !formData.usedFeatures.length)
      )
        return { ...memo, [key]: null };

      if (
        key === 'workArea1' &&
        formData.workArea1 === CUSTOM_WORK_AREA_SELECTION &&
        formData.customWorkArea
      ) {
        return { ...memo, [key]: formData.customWorkArea };
      }

      if (key === 'customWorkArea') return memo;

      if (value !== 'none') return { ...memo, [key]: value };
      return { ...memo, [key]: null };
    }, {});

    try {
      await update(netData);
      trackEvent(event, { actionText: 'edit' });
      close({ modal: 'profile_clinic' });
      showSuccessToast(t('PhysicianClinicProfileEditModule.success.title'), null, {
        toastId: 'multiPatientInviteSuccess',
      });
    } catch (e) {
      e.message = `PhysicianClinicProfileEditModule#handleSubmit(): ${e.message}`;
      Logger.error(e);
      showGenericError();
    }
  };

  const handleClose = e => {
    e.preventDefault();
    close({ modal: 'profile_clinic' });
  };

  const rearrangedCountryList = useMemo(() => {
    const { DE, AT, CH, ...restOfTheWorld } = countries;
    return {
      DE,
      AT,
      CH,
      ...restOfTheWorld,
    };
  }, [countries]);

  return (
    <Modal
      showCloseButton
      className="min-h-0 flex flex-col"
      onClickOutside={handleClose}
      onClickCloseButton={handleClose}
      title={t('PhysicianClinicProfileEditModule.title')}
    >
      <div data-component="PhysicianClinicProfileEditModule">
        <form onSubmit={wrapSubmit(handleSubmit)} className="flex flex-col">
          <p className="text-sm text-slate-500 mb-6">
            {t('PhysicianClinicProfileEditModule.subtitle')}
          </p>
          <ProfileItems>
            <ProfileItem
              className="w-full"
              label={t('PhysicianClinicProfileEditModule.form.workplace.label')}
            >
              <InputField
                placeholder={t('PhysicianClinicProfileEditModule.form.workplace.placeholder')}
                ref={registerFormElement}
                name="workplace"
                defaultValue={workplace}
              />
              <SubTitle Icon={IconUserSolidSquare}>
                {t('PhysicianClinicProfileEditModule.form.workplace.subtitle')}
              </SubTitle>
            </ProfileItem>
            <div />
            <ProfileItem label={t('PhysicianClinicProfileEditModule.form.areaOfActivity')}>
              <DropDown
                name="areaOfActivity"
                ref={registerFormElement}
                options={constant.areaOfActivityIds}
                optionLabels={constant.areaOfActivityIds.map(key => constant.areaOfActivity[key])}
                defaultValue={areaOfActivity}
              />
            </ProfileItem>
            <ProfileItem label={t('PhysicianClinicProfileEditModule.form.workArea')}>
              <DropDown
                name="workArea1"
                ref={registerFormElement}
                defaultValue={isCustomWorkArea(workArea1) ? CUSTOM_WORK_AREA_SELECTION : workArea1}
              >
                <NoSelectionOption />
                {constant.workAreaValues.map(([workArea, subWorkAreas]) => (
                  <Fragment key={`firstLayerCategory#${workArea}`}>
                    <option value={workArea}>{workArea}</option>
                    {Array.isArray(subWorkAreas) &&
                      subWorkAreas.map(secondLevelWorkArea => (
                        <option
                          key={`secondLayerCategory#${secondLevelWorkArea}`}
                          value={secondLevelWorkArea}
                        >
                          &nbsp;&nbsp;&nbsp;&nbsp;
                          {secondLevelWorkArea}
                        </option>
                      ))}
                  </Fragment>
                ))}
              </DropDown>
              <SubTitle Icon={IconUserSolidSquare}>
                {t('PhysicianClinicProfileEditModule.form.subtitle')}
              </SubTitle>
            </ProfileItem>
            {(selectedWorkArea === CUSTOM_WORK_AREA_SELECTION ||
              (isCustomWorkArea(workArea1) && !selectedWorkArea)) && (
              <ProfileItem>
                <InputField
                  placeholder={t(
                    'PhysicianClinicProfileEditModule.form.customWorkArea.placeholder',
                  )}
                  ref={registerFormElement}
                  name="customWorkArea"
                  defaultValue={
                    workArea1 !== CUSTOM_WORK_AREA_SELECTION && isCustomWorkArea(workArea1)
                      ? workArea1
                      : ''
                  }
                />
              </ProfileItem>
            )}
            <ProfileItem label={t('PhysicianClinicProfileEditModule.form.organizationalForm')}>
              <DropDown
                name="organizationalForm"
                ref={registerFormElement}
                defaultValue={organizationalForm}
              >
                <NoSelectionOption />
                {Object.entries(constant.organizationalForm).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </DropDown>
            </ProfileItem>
            <ProfileItem label={t('PhysicianClinicProfileEditModule.form.usedFeatures')}>
              <MultiCheckboxSelect
                name="usedFeatures"
                ref={registerFormElement}
                labels={usedFeaturesFieldData.labels}
                values={usedFeaturesFieldData.values}
                checked={usedFeatures || []}
              />
            </ProfileItem>
          </ProfileItems>
          <p className="text-lg text-slate-500 mb-3">
            {t('PhysicianClinicProfileEditModule.address')}
          </p>
          <ProfileItems>
            <ProfileItem
              label={t('PhysicianClinicProfileEditModule.form.address')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="address"
                autoComplete="street-address"
                defaultValue={address}
              />
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianClinicProfileEditModule.form.address2')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="address2"
                autoComplete="organization"
                defaultValue={address2}
              />
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianClinicProfileEditModule.form.postalCode')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="postalCode"
                autoComplete="postal-code"
                defaultValue={postalCode}
              />
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianClinicProfileEditModule.form.city')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="city"
                autoComplete="address-level2"
                defaultValue={city}
              />
              <SubTitle Icon={IconUserSolidSquare}>
                {t('PhysicianClinicProfileEditModule.form.subtitle')}
              </SubTitle>
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianClinicProfileEditModule.form.country')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <DropDown
                name="countryCode"
                ref={registerFormElement}
                autoComplete="country"
                defaultValue={countryCode}
              >
                <NoSelectionOption />
                <RulerOption />
                {Object.entries(rearrangedCountryList).map(([key, value]) => (
                  <Fragment key={`country#${key}`}>
                    <option value={key} disabled={key === 'ruler'}>
                      {value}
                    </option>
                    {key === 'CH' && <RulerOption />}
                  </Fragment>
                ))}
              </DropDown>
            </ProfileItem>
          </ProfileItems>
          <div className="flex-auto w-full flex justify-center py-2">
            <Button type="submit" isDisabled={isSubmitDisabled}>
              {isUpdating ? <Spinner /> : t('PhysicianClinicProfileEditModule.form.save')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PhysicianClinicProfileEditModule;
